import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aandeelhouder,
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-aandeelhouders',
  templateUrl: './aandeelhouders.component.html',
  styleUrls: ['./aandeelhouders.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class AandeelhoudersComponent implements OnInit, OnDestroy {

  private typeSafeFormShape!: {
    aandeelhouder: FormGroup<{
      bsnNatuurlijkPersoon: FormControl<string | null>;
      naamAandeelhouder: FormControl<string | null>;
      adresAandeelhouder: FormGroup<{
        straat: FormControl<string | null>;
        huisNummer: FormControl<number | null>;
        huisNummerToevoeging: FormControl<string | null>;
        postcode: FormControl<string | null>;
        woonplaats: FormControl<string | null>;
        landCode: FormControl<string | null>;
      }>;
      nominaleWaardeGewoneAandelen: FormControl<number | null>;
      nominaleWaardePreferenteAandelen: FormControl<number | null>;
      nominaleWaardePrioriteitsAandelen: FormControl<number | null>;
      percentageNominaleKapitaal: FormControl<number | null>;
      ontvangenDividend: FormControl<number | null>;
      vorderingOpAandeelhouder: FormControl<number | null>;
      ontvangenRenteVanAandeelhouder: FormControl<number | null>;
      schuldAanAandeelhouder: FormControl<number | null>;
      betaaldeRenteAanAandeelhouder: FormControl<number | null>;
      infokapStortingDoorAandeelhouder: FormControl<number | null>;
    }>;
  };

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public index = 0;
  public dialogVisible = false;
  public optionsLandcodes: SelectFormControlItem<string>[] = [
    {value: 'NLD', label: 'Nederland'},
    {value: 'ALB', label: 'Albanië'},
    {value: 'ARG', label: 'Argentinië'},
    {value: 'ARM', label: 'Armenië'},
    {value: 'ABW', label: 'Aruba'},
    {value: 'AUS', label: 'Australië'},
    {value: 'AZE', label: 'Azerbeidjan'},
    {value: 'BHR', label: 'Bahrein'},
    {value: 'BGD', label: 'Bangladesh'},
    {value: 'BRB', label: 'Barbedos'},
    {value: 'BLR', label: 'Belarus (Wit-Rusland)'},
    {value: 'BEL', label: 'België'},
    {value: 'BMU', label: 'Bermuda'},
    {value: 'BES', label: 'Bonaire, St. Eustatius en Saba'},
    {value: 'BIH', label: 'Bosnië-Herzegovina'},
    {value: 'BRA', label: 'Brazilië'},
    {value: 'BGR', label: 'Bulgarije'},
    {value: 'CAN', label: 'Canada'},
    {value: 'CHN', label: 'China'},
    {value: 'CUW', label: 'Curaçao'},
    {value: 'CYP', label: 'Cyprus'},
    {value: 'DNK', label: 'Denemarken'},
    {value: 'DEU', label: 'Duitsland'},
    {value: 'EGY', label: 'Egypte'},
    {value: 'EST', label: 'Estland'},
    {value: 'PHL', label: 'Filippijnen'},
    {value: 'FIN', label: 'Finland'},
    {value: 'FRA', label: 'Frankrijk'},
    {value: 'GEO', label: 'Georgië'},
    {value: 'GHA', label: 'Ghana'},
    {value: 'GRC', label: 'Griekenland'},
    {value: 'HUN', label: 'Hongarije'},
    {value: 'HKG', label: 'Hongkong'},
    {value: 'IRL', label: 'Ierland'},
    {value: 'ISL', label: 'IJsland'},
    {value: 'IND', label: 'India'},
    {value: 'IDN', label: 'Indonesië'},
    {value: 'ISR', label: 'Israël'},
    {value: 'ITA', label: 'Italië'},
    {value: 'JPN', label: 'Japan'},
    {value: 'JOR', label: 'Jordanië'},
    {value: 'KAZ', label: 'Kazachstan'},
    {value: 'KGZ', label: 'Kirgizië'},
    {value: 'KGZ', label: 'Kirgizië'},
    {value: 'KWT', label: 'Koeweit'},
    {value: 'HRV', label: 'Kroatië'},
    {value: 'LVA', label: 'Letland'},
    {value: 'LTU', label: 'Litouwen'},
    {value: 'LUX', label: 'Luxemburg'},
    {value: 'MKD', label: 'Macedonië'},
    {value: 'MWI', label: 'Malawi'},
    {value: 'MYS', label: 'Maleisië'},
    {value: 'MLT', label: 'Malta'},
    {value: 'MAR', label: 'Marokko'},
    {value: 'MEX', label: 'Mexico'},
    {value: 'MDA', label: 'Moldavië'},
    {value: 'MOZ', label: 'Mozambique'},
    {value: 'NZL', label: 'Nieuw-Zeeland'},
    {value: 'NGA', label: 'Nigeria'},
    {value: 'NOR', label: 'Noorwegen'},
    {value: 'UKR', label: 'Oekraïne'},
    {value: 'UZB', label: 'Oezbekistan'},
    {value: 'OMN', label: 'Oman'},
    {value: 'AUT', label: 'Oostenrijk'},
    {value: 'PAK', label: 'Pakistan'},
    {value: 'PAN', label: 'Panama'},
    {value: 'POL', label: 'Polen'},
    {value: 'PRT', label: 'Portugal'},
    {value: 'QAT', label: 'Qatar'},
    {value: 'ROU', label: 'Roemenië'},
    {value: 'RUS', label: 'Rusland'},
    {value: 'SAU', label: 'Saudi-Arabië'},
    {value: 'SRB', label: 'Servië'},
    {value: 'SGP', label: 'Singapore'},
    {value: 'SXM', label: 'Sint Maarten'},
    {value: 'SVN', label: 'Slovenië'},
    {value: 'SVK', label: 'Slowakije'},
    {value: 'ESP', label: 'Spanje'},
    {value: 'LKA', label: 'Sri Lanka'},
    {value: 'SUR', label: 'Suriname'},
    {value: 'TJK', label: 'Tadzjikistan'},
    {value: 'TWN', label: 'Taiwan'},
    {value: 'THA', label: 'Thailand'},
    {value: 'CZE', label: 'Tsjechië'},
    {value: 'TUN', label: 'Tunesië'},
    {value: 'TUR', label: 'Turkije'},
    {value: 'UGA', label: 'Uganda'},
    {value: 'VEN', label: 'Venezuela'},
    {value: 'GBR', label: 'Verenigd Koninkrijk'},
    {value: 'ARE', label: 'Verenigde Arabische Emiraten'},
    {value: 'USA', label: 'Verenigde Staten van Amerika'},
    {value: 'VNM', label: 'Vietnam'},
    {value: 'ZMB', label: 'Zambia'},
    {value: 'ZWE', label: 'Zimbabwe'},
    {value: 'ZAF', label: 'Zuid-Afrika'},
    {value: 'KOR', label: 'Zuid-Korea'},
    {value: 'SWE', label: 'Zweden'},
    {value: 'CHE', label: 'Zwitserland'}
  ];

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public aandeelhoudersFg = new FormGroup({
    aandeelhouders: new FormArray<typeof this.typeSafeFormShape.aandeelhouder>([])
  });

  showDialog(index: number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  get aandeelhouders(): FormArray<typeof this.typeSafeFormShape.aandeelhouder> {
    return this.aandeelhoudersFg.controls.aandeelhouders;
  }

  addRij(): void {
    this.aandeelhouders.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.aandeelhouders.removeAt(index);
    this.saveForm();
  }

  createFormGroup(item?: Aandeelhouder): typeof this.typeSafeFormShape.aandeelhouder {
    return new FormGroup({
      bsnNatuurlijkPersoon: new FormControl<string | null>(item?.bsnNatuurlijkPersoon ?? null, {
        validators: [Validators.pattern(/^\d{9}$/),
        ]
      }),
      naamAandeelhouder: new FormControl<string | null>(item?.naamAandeelhouder ?? null, {
        validators: [Validators.required]
      }),
      adresAandeelhouder: new FormGroup({
        straat: new FormControl<string | null>(item?.adresAandeelhouder?.straat ?? null, {
          validators: [Validators.required]
        }),
        huisNummer: new FormControl<number | null>(item?.adresAandeelhouder?.huisNummer ?? null, {
          validators: [Validators.required]
        }),
        huisNummerToevoeging: new FormControl<string | null>(item?.adresAandeelhouder?.huisNummerToevoeging ?? null),
        postcode: new FormControl<string | null>(item?.adresAandeelhouder?.postcode ?? null, {
          validators: [Validators.required]
        }),
        woonplaats: new FormControl<string | null>(item?.adresAandeelhouder?.woonplaats ?? null, {
          validators: [Validators.required]
        }),
        landCode: new FormControl<string | null>(item?.adresAandeelhouder?.landCode ?? null, {
          validators: [Validators.required]
        }),
      }),
      nominaleWaardeGewoneAandelen: new FormControl<number | null>(item?.nominaleWaardeGewoneAandelen ?? null, {
        validators: [Validators.required]
      }),
      nominaleWaardePreferenteAandelen: new FormControl<number | null>(item?.nominaleWaardePreferenteAandelen ?? null),
      nominaleWaardePrioriteitsAandelen: new FormControl<number | null>(item?.nominaleWaardePrioriteitsAandelen ?? null),
      percentageNominaleKapitaal: new FormControl<number | null>(item?.percentageNominaleKapitaal ?? null, {
        validators: [Validators.required]
      }),
      ontvangenDividend: new FormControl<number | null>(item?.ontvangenDividend ?? null),
      vorderingOpAandeelhouder: new FormControl<number | null>(item?.vorderingOpAandeelhouder ?? null),
      ontvangenRenteVanAandeelhouder: new FormControl<number | null>(item?.ontvangenRenteVanAandeelhouder ?? null),
      schuldAanAandeelhouder: new FormControl<number | null>(item?.schuldAanAandeelhouder ?? null),
      betaaldeRenteAanAandeelhouder: new FormControl<number | null>(item?.betaaldeRenteAanAandeelhouder ?? null),
      infokapStortingDoorAandeelhouder: new FormControl<number | null>(item?.infokapStortingDoorAandeelhouder ?? null)
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.aandeelhoudersFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieAandeelhouders || [];
    const formArray = this.aandeelhouders;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      const ah = lijst[i];

      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(ah, {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(ah), {emitEvent: false});
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.aandeelhoudersFg.updateValueAndValidity({emitEvent: false});

    const aandeelhouders: Aandeelhouder[] = (this.aandeelhoudersFg.getRawValue().aandeelhouders || []).map(ah => ({
      bsnNatuurlijkPersoon: ah.bsnNatuurlijkPersoon ?? undefined,
      naamAandeelhouder: ah.naamAandeelhouder ?? undefined,
      adresAandeelhouder: {
        straat: ah.adresAandeelhouder?.straat ?? undefined,
        huisNummer: ah.adresAandeelhouder?.huisNummer ?? undefined,
        huisNummerToevoeging: ah.adresAandeelhouder?.huisNummerToevoeging ?? undefined,
        postcode: this.formatPostcode(ah.adresAandeelhouder?.postcode) ?? undefined,
        woonplaats: ah.adresAandeelhouder?.woonplaats ?? undefined,
        landCode: ah.adresAandeelhouder?.landCode ?? undefined
      },
      nominaleWaardeGewoneAandelen: ah.nominaleWaardeGewoneAandelen ?? undefined,
      nominaleWaardePreferenteAandelen: ah.nominaleWaardePreferenteAandelen ?? undefined,
      nominaleWaardePrioriteitsAandelen: ah.nominaleWaardePrioriteitsAandelen ?? undefined,
      percentageNominaleKapitaal: ah.percentageNominaleKapitaal != null
        ? Math.round(ah.percentageNominaleKapitaal * 100) / 100
        : undefined,
      ontvangenDividend: ah.ontvangenDividend ?? undefined,
      vorderingOpAandeelhouder: ah.vorderingOpAandeelhouder ?? undefined,
      ontvangenRenteVanAandeelhouder: ah.ontvangenRenteVanAandeelhouder ?? undefined,
      schuldAanAandeelhouder: ah.schuldAanAandeelhouder ?? undefined,
      betaaldeRenteAanAandeelhouder: ah.betaaldeRenteAanAandeelhouder ?? undefined,
      infokapStortingDoorAandeelhouder: ah.infokapStortingDoorAandeelhouder ?? undefined
    }));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieAandeelhouders: aandeelhouders
        }
      } as CorporateTaxEntity;
    });
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) {
      return '';
    }

    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, '');
      const letters = cleanedPostcode.substring(4, 6).replace(/[^a-zA-Z]/g, '').toUpperCase();
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode;
  }
}
